import _ from "lodash";
import * as React from "react";

import ImageCropModal from "./ImageCropModal";

interface IProps {
    errorText: string;
    imageCaption: string;
    imageCaptionMaxLength: number;
    imageFileName: string;
    imageFileSizeLimit: number;
    onDeleteImage: () => void;
    onSubmitImage: (file: File, x: number, y: number, w: number, h: number) => void;
    originalImagePath: string;
    setImageCaption: (newCaption: string) => void;
    targetHeight: number;
    targetWidth: number;
}

interface IState {
    croppedBlobUrl: string;
    imageBlobUrl: string;
    isCropModalOpen: boolean;
    selectedFile: File;
}

export default class ImagePickerWithPreview extends React.Component<IProps, IState> {
    private uploadInput: HTMLInputElement;

    public constructor(props, context) {
        super(props, context);

        this.state = {
            croppedBlobUrl: null,
            imageBlobUrl: null,
            isCropModalOpen: false,
            selectedFile: null,
        };
    }

    public render() {
        return (
            <>
                <this.uploadButtonOrPreview />

                <input
                    type="file"
                    accept="image/*"
                    ref={(ref) => this.uploadInput = ref}
                    hidden
                    onChange={(e) => {
                        const file = e.target.files[0];
                        this.onImageUpload(file);
                    }}
                />

                <ImageCropModal
                    imageUrl={this.state.imageBlobUrl}
                    isOpen={this.state.isCropModalOpen}
                    onRequestClose={() => {
                        this.uploadInput.value = null;
                        this.setState({isCropModalOpen: false});
                    }}
                    onSubmitCrop={(x: number, y: number, w: number, h: number, croppedBlobUrl: string) => {
                        this.setState({
                            croppedBlobUrl,
                            isCropModalOpen: false,
                        });
                        this.props.onSubmitImage(this.state.selectedFile, x, y, w, h);
                    }}
                    targetHeight={this.props.targetHeight}
                    targetWidth={this.props.targetWidth}
                />
            </>
        );
    }

    private uploadButtonOrPreview = () => {
        if (_.isEmpty(this.props.imageFileName)) {
            return (
                <this.uploadButton />
            );
        }

        return (
            <div className="feature-image">
                <this.imagePreview />
                <this.captionField />
                <this.errorMessage />
            </div>
        );
    }

    private uploadButton = () => {
        return (
            <button
                className="admin-btn nobg-btn border-btn"
                type="button"
                aria-label="Add Feature Image"
                onClick={(e) => {
                    e.preventDefault();
                    this.uploadInput.click();
                }}
            >
                <span className="fa fa-picture-o" aria-hidden="true"></span>
                <span>Add Feature Image</span>
            </button>
        );
    }

    private imagePreview = () => {
        return (
            <div className="image">
                <div className="admin-buttons">
                    <button
                        className="admin-btn nobg-btn border-btn"
                        type="button"
                        aria-label="Remove Feature Image"
                        onClick={(e) => {
                            e.preventDefault();
                            this.uploadInput.value = null;
                            this.props.onDeleteImage();
                        }}
                    >
                        <span className="fa fa-trash" aria-hidden="true"></span>
                    </button>
                </div>
                <img
                    src={this.props.originalImagePath || this.state.croppedBlobUrl}
                    alt="Feature image preview"
                    title="Feature image" />
            </div>
        );
    }

    private captionField = () => {
        return (
            <div className="img-caption">
                <input
                    aria-label="Image caption"
                    type="text"
                    id="caption"
                    name="caption"
                    placeholder="Click here to add caption..."
                    value={this.props.imageCaption}
                    maxLength={this.props.imageCaptionMaxLength}
                    onChange={(e) => {
                        this.props.setImageCaption(e.target.value);
                    }}
                />
            </div>
        );
    }

    private errorMessage = () => {
        if (!this.props.errorText || this.props.errorText === "") {
            return null;
        }

        return (
            <div className="field-error">
                {this.props.errorText}
            </div>
        );
    }

    private onImageUpload = (file: File) => {
        const fileName = file.name;
        const fileExt = fileName.includes(".") ? fileName.substring(fileName.lastIndexOf(".") + 1).toUpperCase() : "";
        const allowExts = ["BMP", "GIF", "JPG", "JPEG", "PNG"];

        if (!allowExts.includes(fileExt)) {
            alert(`Invalid image type: ${file.type}. Only BMP, GIF, JPG, JPEG, and PNG files are accepted.`);
            return;
        }

        if (file.size > this.props.imageFileSizeLimit * 1024 * 1024) {
            alert(`File size cannot be larger than ${this.props.imageFileSizeLimit} MB.`);
            return;
        }

        const url = URL.createObjectURL(file);
        this.setState({selectedFile: file, imageBlobUrl: url, isCropModalOpen: true});
    }
}
